import type { ReactNode } from 'react';
import React, { useEffect, useCallback, forwardRef, useRef } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';
import { N0, N40, N800 } from '@atlaskit/theme/colors';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ErrorDisplay } from '@confluence/error-boundary';

import { useHomeWidgetState } from './useHomeWidgetState';
import { HomeWidgetState } from './__types__/HomeWidgetsQuery';

type CollapsibleSectionProps = {
	sectionId: string;
	header: ReactNode;
	headerActions?: ReactNode; // absolute positioned to avoid nesting buttons in header for better accessibility
	defaultState?: HomeWidgetState;
	initialStateOverride?: HomeWidgetState;
	onInitialCollapse?: () => void;
	spotlightName?: string;
	ariaLabel?: string;
	children: ReactNode;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const Collapsible = styled.div<{
	isCollapsed: boolean;
}>`
	background-color: ${token('elevation.surface.raised', N0)};
	position: relative;
	margin-bottom: ${token('space.150', '12px')};
	border: 1px solid ${token('color.border', N40)};
	border-radius: ${token('border.radius.200', '8px')};
	&:hover {
		${(props) =>
			props.isCollapsed
				? `box-shadow: ${token(
						'elevation.shadow.raised',
						'0px 1px 1px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)',
					)}`
				: ``}
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const HeaderExpandCollapseButton = styled.button<{
	isCollapsed: boolean;
}>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	background-color: ${token('elevation.surface.raised', N0)};
	color: ${token('color.text', N800)};
	font-size: 14px;
	font-weight: 500;
	padding: ${token('space.100', '8px')};
	margin: 0;
	position: sticky;
	top: ${token('space.negative.150', '-12px')};
	z-index: 1;
	border: none;

	border-top-left-radius: token('border.radius.200', '8px');
	border-top-right-radius: token('border.radius.200', '8px');

	${({ isCollapsed }) =>
		isCollapsed
			? `
      border-radius: ${token('border.radius.200', '8px')};`
			: `
      border-bottom: 'none'
  `}

	:hover {
		cursor: pointer;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const HeaderActionsWrapper = styled.span`
	position: absolute;
	right: ${token('space.400', '32px')};
	top: ${token('space.100', '8px')};
	z-index: 1;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const CollapsibleContent = styled.div`
	padding: ${token('space.100', '8px')};
`;

/**
 * CollapsibleSection should be used within the right rail on the home screen. All sections of the right rail should
 * be placed inside of a CollapsibleSection. Local storage and collapsed state will be handled within this component.
 *
 * By default the header will be displayed when the section is expanded, to hide the header use the prop
 * hideHeaderWhenExpanded. If hideHeaderWhenExpanded is false, the content of the section will occupy the full width
 * with 24px of left and right padding. If hidHeaderWhenExpanded is true the content will only occupy the space left
 * of the expand/collapse button with 24px of left padding and 40px of right padding.
 *
 * @param sectionId: Unique identifier for the section, used for local storage
 * @param header: React node to appear in the header. By default the header will be rendered in the expanded state
 * @param hideHeaderWhenExpanded: Boolean value that, when true, will hide the header when the section is expanded
 * @param children: React node to be rendered in the expanded state. Children must be styled appropriately.
 */
export const CollapsibleSection = forwardRef<HTMLDivElement, CollapsibleSectionProps>(
	(
		{
			sectionId,
			header,
			headerActions,
			children,
			defaultState,
			initialStateOverride,
			onInitialCollapse,
			spotlightName,
			ariaLabel,
		},
		ref,
	) => {
		const { widgetState, setWidgetState, loading, error } = useHomeWidgetState(
			sectionId,
			defaultState,
			initialStateOverride,
		);
		const isCollapsed = widgetState === HomeWidgetState.COLLAPSED;
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const checkedInitialState = useRef(false);
		useEffect(() => {
			if (loading || checkedInitialState.current) return;
			checkedInitialState.current = true;
			if (widgetState === HomeWidgetState.COLLAPSED) {
				onInitialCollapse?.();
			}
		}, [loading, widgetState, onInitialCollapse]);

		const fireAnalyticsEvent = useCallback(
			(action: 'collapsed' | 'expanded') => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action,
						actionSubject: 'collapsibleSection',
						actionSubjectId: sectionId,
						source: 'rightPanel',
						attributes: {
							homeVersion: '1.5',
						},
					},
				}).fire();
			},
			[createAnalyticsEvent, sectionId],
		);

		const onClick = useCallback(() => {
			const onCollapse = () => {
				setWidgetState(HomeWidgetState.COLLAPSED);
				fireAnalyticsEvent('collapsed');
			};

			const onExpand = () => {
				setWidgetState(HomeWidgetState.EXPANDED);
				fireAnalyticsEvent('expanded');
			};

			isCollapsed ? onExpand() : onCollapse();
		}, [isCollapsed, setWidgetState, fireAnalyticsEvent]);

		if (loading) {
			return null;
		}

		const Icon = isCollapsed ? ChevronRightIcon : ChevronDownIcon;

		const headerId = `home-right-panel-header-${sectionId}`;

		const sectionHeader = (
			<>
				<HeaderExpandCollapseButton
					id={headerId}
					onClick={onClick}
					isCollapsed={isCollapsed}
					aria-expanded={!isCollapsed}
					aria-controls="collapsible-content"
					aria-label={ariaLabel}
				>
					{header}
					<Icon label="" LEGACY_size="medium" />
				</HeaderExpandCollapseButton>
				<HeaderActionsWrapper>{headerActions}</HeaderActionsWrapper>
			</>
		);

		return (
			<Collapsible isCollapsed={isCollapsed} ref={ref} data-testid={sectionId}>
				{spotlightName ? (
					<SpotlightTarget name={spotlightName}>{sectionHeader}</SpotlightTarget>
				) : (
					sectionHeader
				)}
				{isCollapsed ? null : (
					<CollapsibleContent id="collapsible-content" role="region" aria-labelledby={headerId}>
						{children}
					</CollapsibleContent>
				)}
				{error && <ErrorDisplay error={error} />}
			</Collapsible>
		);
	},
);
